// src/pages/help/HelpSection.tsx
// Vista de una sección específica del Centro de Ayuda

import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { helpSections } from '../../data/helpCenterData';
import { useUser } from '../../context/UserContext';

import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function HelpSection() {
  const { sectionId } = useParams<{ sectionId: string }>();
  const { user } = useUser();

  // Verificar permisos del usuario
  const isAdmin = user?.rol === 'ADMIN';
  const isAdminComite = user?.esAdminComite === true;

  const section = helpSections.find(s => s.id === sectionId);

  // Verificar si el usuario tiene acceso a esta sección
  const hasAccess = useMemo(() => {
    if (!section) return false;

    // Sección de admin solo para admins
    if (section.id === 'admin-guide' && !isAdmin) {
      return false;
    }
    // Sección de comité solo para admins y admin de comité
    if (section.id === 'committee-guide' && !isAdmin && !isAdminComite) {
      return false;
    }
    return true;
  }, [section, isAdmin, isAdminComite]);

  // Filtrar otras secciones según permisos
  const filteredOtherSections = useMemo(() => {
    return helpSections.filter(s => {
      if (s.id === sectionId || s.id === 'faq') return false;
      if (s.id === 'admin-guide' && !isAdmin) return false;
      if (s.id === 'committee-guide' && !isAdmin && !isAdminComite) return false;
      return true;
    });
  }, [sectionId, isAdmin, isAdminComite]);

  // Sección no encontrada
  if (!section) {
    return (
      <>
        <Navbar transparent={false} />
        <div className="container py-5">
          <div className="text-center">
            <i className="fa-solid fa-exclamation-circle fa-4x text-warning mb-4"></i>
            <h2>Sección no encontrada</h2>
            <p className="text-muted">La sección que buscas no existe o fue movida.</p>
            <Link to="/ayuda" className="btn btn-primary">
              <i className="fa-solid fa-arrow-left me-2"></i>
              Volver al Centro de Ayuda
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Sin acceso a esta sección
  if (!hasAccess) {
    return (
      <>
        <Navbar transparent={false} />
        <div className="container py-5">
          <div className="text-center">
            <i className="fa-solid fa-lock fa-4x text-danger mb-4"></i>
            <h2>Acceso Restringido</h2>
            <p className="text-muted">No tienes permisos para ver esta sección del manual.</p>
            <p className="text-muted small">
              {sectionId === 'admin-guide' && 'Esta sección está disponible solo para administradores.'}
              {sectionId === 'committee-guide' && 'Esta sección está disponible solo para administradores y miembros del comité de eventos.'}
            </p>
            <Link to="/ayuda" className="btn btn-primary">
              <i className="fa-solid fa-arrow-left me-2"></i>
              Volver al Centro de Ayuda
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar transparent={false} />

      {/* Header de la sección */}
      <section className="py-5 bg-primary">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Centro de Ayuda', path: '/ayuda' },
              { label: section.title }
            ]}
            light={true}
          />

          <div className="row align-items-center mt-4">
            <div className="col-auto">
              <span className="d-inline-flex align-items-center justify-content-center bg-white rounded-circle" style={{ width: '80px', height: '80px' }}>
                <i className={`${section.icon} fa-2x text-primary`}></i>
              </span>
            </div>
            <div className="col">
              <h1 className="text-white mb-2">{section.title}</h1>
              <p className="text-white-50 mb-0">{section.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Lista de artículos */}
      <section className="gray-simple py-5">
        <div className="container">
          <div className="row">
            {/* Sidebar de navegación */}
            <div className="col-lg-3 mb-4 mb-lg-0">
              <div className="card border-0 shadow-sm sticky-top" style={{ top: '100px' }}>
                <div className="card-header bg-white border-0">
                  <h6 className="mb-0">
                    <i className="fa-solid fa-list me-2"></i>
                    En esta sección
                  </h6>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {section.articles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/ayuda/articulo/${article.id}`}
                        className="list-group-item list-group-item-action d-flex align-items-center"
                      >
                        <span className="badge bg-primary bg-opacity-10 text-primary me-2">
                          {index + 1}
                        </span>
                        <span className="small">{article.title}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Otras secciones */}
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-header bg-white border-0">
                  <h6 className="mb-0">
                    <i className="fa-solid fa-folder me-2"></i>
                    Otras secciones
                  </h6>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {filteredOtherSections.map((s) => (
                      <Link
                        key={s.id}
                        to={`/ayuda/seccion/${s.id}`}
                        className="list-group-item list-group-item-action d-flex align-items-center"
                      >
                        <i className={`${s.icon} text-muted me-2`}></i>
                        <span className="small">{s.title}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Contenido principal */}
            <div className="col-lg-9">
              <div className="row g-4">
                {section.articles.length === 0 ? (
                  <div className="col-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body text-center py-5">
                        <i className="fa-solid fa-file-circle-question fa-4x text-muted mb-3"></i>
                        <h5>No hay artículos en esta sección</h5>
                        <p className="text-muted">Pronto agregaremos contenido aquí.</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  section.articles.map((article, index) => (
                    <div className="col-md-6" key={article.id}>
                      <Link to={`/ayuda/articulo/${article.id}`} className="text-decoration-none">
                        <div className="card border-0 shadow-sm h-100 hover-shadow transition-all">
                          <div className="card-body">
                            <div className="d-flex align-items-start">
                              <span className="badge bg-primary bg-opacity-10 text-primary me-3 mt-1" style={{ minWidth: '28px' }}>
                                {index + 1}
                              </span>
                              <div>
                                <h6 className="card-title text-dark mb-2">{article.title}</h6>
                                <p className="card-text text-muted small mb-2">
                                  {article.content.substring(0, 120).replace(/[#*`\n]/g, ' ').trim()}...
                                </p>
                                <div className="d-flex flex-wrap gap-1">
                                  {article.tags.slice(0, 3).map((tag, i) => (
                                    <span key={i} className="badge bg-light text-muted small">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="card-footer bg-white border-0 pt-0">
                            <span className="text-primary small">
                              Leer artículo <i className="fa-solid fa-arrow-right ms-1"></i>
                            </span>
                          </div>
                        </div>
                      </Link>
                    </div>
                  ))
                )}
              </div>

              {/* Navegación entre secciones */}
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <p className="text-muted small mb-1">¿No encontraste lo que buscabas?</p>
                      <Link to="/ayuda/faq" className="text-primary">
                        <i className="fa-solid fa-circle-question me-2"></i>
                        Ver Preguntas Frecuentes
                      </Link>
                    </div>
                    <div className="col-md-6 text-md-end">
                      <Link to="/contact" className="btn btn-outline-primary btn-sm">
                        <i className="fa-solid fa-envelope me-2"></i>
                        Contactar Soporte
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />

      <style>{`
        .hover-shadow:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
        }
        .transition-all {
          transition: all 0.3s ease;
        }
      `}</style>
    </>
  );
}
